import { DISCOVER_NODE_COMMAND, DISCOVERY_PROMOTE_COMMAND, DISCOVERY_QUERY, } from "@econ-v1/rpc";
import { NodeOnboardingChromeElement } from "./onboarding-chrome.js";
import { OnboardingController } from "./onboarding-controller.js";
import { NodeOnboardingConnectDiscoveryElement, } from "./screens/connect-discovery.js";
import { NodeOnboardingIdentifyIdentityElement } from "./screens/identify-identity.js";
import { NodeOnboardingIdentifyInvitationElement } from "./screens/identify-invitation.js";
import { NodeOnboardingReadyCompleteElement } from "./screens/ready-complete.js";
import { NodeOnboardingSecureMatchcodeElement } from "./screens/secure-matchcode.js";
/**
 * Onboarding gate — the Task 8 integration surface. Drives an in-process
 * `OnboardingController`, renders `<node-onboarding-chrome>` plus whichever screen matches
 * `view.screen`, and maps each screen's DOM events onto `OnboardingCommand`s the way
 * `../pairing/pairing-gate.ts` maps pairing UI events onto pairing RPC calls.
 *
 * KERNEL WIRING IS DELIBERATELY OUT OF SCOPE for the rest of the flow. The
 * `onboarding.status.v1` / `onboarding.command.v1` / `onboarding.events.v1` RPC seam
 * (`@econ-v1/rpc`'s `onboarding-contract.ts`) is not called from this element yet — the
 * flow runs entirely client-side against the local `OnboardingController`. The seam this
 * class exposes for that follow-up is `#applyCommand`: every screen event funnels through
 * it before touching the controller, so swapping the body of that one method for
 * `this.#client.command(ONBOARDING_COMMAND, command)` (plus subscribing `view` from
 * `onboarding.events.v1`/`onboarding.status.v1` instead of `controller.onChange`) is the
 * entire follow-up change — nothing above it (the event listeners wired in
 * `connectedCallback`) needs to move.
 *
 * The #1815 "search the node on the LAN" discovery step is the one exception: it IS
 * kernel-wired for real, over the `onboarding.discovery.v1` seam
 * (`@econ-v1/rpc`'s `discovery-contract.ts`) — `#subscribeDiscovery` drives a live query of
 * `DiscoveryCandidateView` rows, and `set-up` only advances — and only promotes the
 * candidate as the home-node route via `DISCOVERY_PROMOTE_COMMAND` — when the live view
 * currently holds a `"verified"` candidate. Reachability alone never advances (fail-closed).
 *
 * The discovery screen's manual-address path (lan-direct-pairing plan, task C2) is wired
 * separately: `discover-node` (from the screen's manual-address form / Retry buttons) drives
 * `#discoverNode`, which issues the pre-pairing, session-free `DISCOVER_NODE_COMMAND` — NOT
 * `DISCOVERY_PROBE_COMMAND` above, which re-proves a candidate against an already-known
 * home-node id and doesn't apply to a cold, typed-in address. `#manualDiscovery` tracks the
 * in-flight/last outcome (entering `"probing"` synchronously before the command resolves) and
 * feeds it back to the screen as `DiscoveryState.manual`. The screen's resulting
 * `node-discovered` event (on a `"verified"` outcome's "Continue") is deliberately left
 * unhandled here — it bubbles+composes straight past this gate for whatever actually sets
 * the home node (C3) to consume; this class never persists it.
 *
 * `#manualTrustModeByAddress` (Tier-0 trusted-IPv6 e2e plan, Task 2) remembers the
 * `trustMode` a `"verified"` result last proved for an address, and re-attaches it to every
 * later `ManualDiscoveryState` for that same address — including a subsequent
 * `"unreachable-or-untrusted"` retry — so the screen can tell a publicly-trusted origin's
 * transient failure apart from an actual untrusted-certificate rejection and skip the cert
 * guidance branch accordingly.
 */
const CHROME_TAG = "node-onboarding-chrome";
const DISCOVERY_TAG = "node-onboarding-connect-discovery";
const INVITATION_TAG = "node-onboarding-identify-invitation";
const IDENTITY_TAG = "node-onboarding-identify-identity";
const MATCHCODE_TAG = "node-onboarding-secure-matchcode";
const READY_TAG = "node-onboarding-ready-complete";
const GATE_TAG = "node-onboarding-gate";
/**
 * `handoff` (the wifi scenario's credential-entry step) has no screen element yet — only
 * the five screens the onboarding UI build produced (connect/invite/identity/matchcode/
 * ready) exist as of this task. The outlet renders nothing for it rather than guessing at
 * a UI that hasn't been built; wiring a wifi-handoff screen is a separate follow-up.
 */
const SCREEN_TAG_BY_SCREEN = {
    done: READY_TAG,
    handoff: undefined,
    identity: IDENTITY_TAG,
    invite: INVITATION_TAG,
    matchcode: MATCHCODE_TAG,
    scan: DISCOVERY_TAG,
};
const DEFAULT_DISCOVERY_STEPS = [
    { label: "LISTENING ON YOUR NETWORK", meta: "MDNS", status: "done" },
    { label: "QUERYING NEARBY DEVICES", meta: "SUBNET", status: "active" },
    { label: "CHECKING WHO OWNS IT", status: "pending" },
];
const DISCOVERY_STEPS_FOUND = [
    { label: "LISTENING ON YOUR NETWORK", meta: "MDNS", status: "done" },
    { label: "QUERYING NEARBY DEVICES", meta: "SUBNET", status: "done" },
    { label: "CHECKING WHO OWNS IT", status: "done" },
];
export class NodeOnboardingGateElementImpl extends HTMLElement {
    #controller = new OnboardingController();
    #discoveryRows = new Map();
    #manualTrustModeByAddress = new Map();
    #client;
    #discoverySubscription;
    #manualDiscovery;
    #scenario = "new";
    #started = false;
    set client(value) {
        this.#client = value;
        this.#subscribeDiscovery();
    }
    get client() {
        return this.#client;
    }
    set scenario(value) {
        this.#scenario = value;
        if (this.#started)
            this.#controller.start(value);
    }
    get scenario() {
        return this.#scenario;
    }
    connectedCallback() {
        registerOnboardingElements();
        if (!this.shadowRoot) {
            const root = this.attachShadow({ mode: "open" });
            root.innerHTML = `
        <div class="onboarding-gate">
          <${CHROME_TAG}></${CHROME_TAG}>
          <div class="onboarding-gate-outlet" data-screen-outlet></div>
        </div>
      `;
            root.addEventListener("set-up", () => this.#confirmDiscoveredNode());
            // "My node isn't nearby" reveals the discovery screen's own manual-address form
            // (`connect-discovery.ts`'s `#manualEntryOpen`) — this listener has nothing left to
            // do; `discover-node` (the form's actual submit, or a Retry button) is what drives
            // the manual-address probe.
            root.addEventListener("choose-another", () => { });
            root.addEventListener("discover-node", (event) => {
                const address = event.detail.address;
                this.#discoverNode(address);
            });
            root.addEventListener("redeem", (event) => {
                const code = event.detail.code;
                this.#applyCommand({ code, kind: "redeem-invitation" });
            });
            root.addEventListener("skip", () => this.#applyCommand({ kind: "skip-invitation" }));
            root.addEventListener("submit-identity", (event) => {
                const detail = event.detail;
                this.#applyCommand({ ...detail, kind: "submit-identity" });
            });
            root.addEventListener("confirm-matchcode", () => this.#applyCommand({ kind: "confirm-matchcode" }));
            root.addEventListener("restart", () => this.#applyCommand({ kind: "choose-path", scenario: "new" }));
            root.addEventListener("enter", () => {
                this.dispatchEvent(new CustomEvent("onboarding-complete", { bubbles: true, composed: true }));
            });
            this.#controller.onChange(() => this.#render());
        }
        if (!this.#started) {
            this.#started = true;
            this.#controller.start(this.#scenario);
        }
        this.#render();
    }
    disconnectedCallback() {
        this.#discoverySubscription?.();
        this.#discoverySubscription = undefined;
    }
    #applyCommand(command) {
        this.#controller.apply(command);
    }
    /**
     * The #1815 discovery live-query subscription. Re-subscribes whenever `client` changes
     * (mirrors `../pairing/pairing-gate.ts`'s `set client` re-initialization) and drops any
     * rows from a previous client so a stale probe from an earlier session never lingers.
     */
    #subscribeDiscovery() {
        this.#discoverySubscription?.();
        this.#discoverySubscription = undefined;
        this.#discoveryRows.clear();
        if (this.#client === undefined)
            return;
        this.#discoverySubscription = this.#client.liveQuery(DISCOVERY_QUERY, {}, (diff) => {
            this.#applyDiscoveryDiff(diff);
            this.#render();
        });
    }
    #applyDiscoveryDiff(diff) {
        for (const row of diff.added)
            this.#discoveryRows.set(row.address, row);
        for (const row of diff.updated)
            this.#discoveryRows.set(row.address, row);
        for (const row of diff.deleted)
            this.#discoveryRows.delete(row.address);
    }
    /**
     * Drives the manual-address discovery screen's `discover-node` event: enters `"probing"`
     * synchronously (so the screen can render a live indicator before the round trip resolves),
     * then maps `DISCOVER_NODE_COMMAND`'s `NodeDiscoveryResult` onto the screen's
     * `ManualDiscoveryState`. If `#client` is swapped mid-flight (e.g. a re-pair), the stale
     * result is dropped rather than clobbering whatever the new client is doing.
     */
    #discoverNode(address) {
        const trimmed = address.trim();
        if (trimmed.length === 0)
            return;
        this.#manualDiscovery = { address: trimmed, status: "probing" };
        this.#render();
        const client = this.#client;
        if (client === undefined) {
            this.#manualDiscovery = this.#withKnownTrustMode(trimmed, { address: trimmed, status: "unreachable-or-untrusted" });
            this.#render();
            return;
        }
        void client.command(DISCOVER_NODE_COMMAND, { address: trimmed })
            .then((result) => {
            if (this.#client !== client)
                return;
            if (result.status === "verified")
                this.#manualTrustModeByAddress.set(trimmed, result.candidate.trustMode);
            this.#manualDiscovery = this.#withKnownTrustMode(trimmed, manualDiscoveryFromResult(trimmed, result));
            this.#render();
        })
            .catch(() => {
            if (this.#client !== client)
                return;
            this.#manualDiscovery = this.#withKnownTrustMode(trimmed, { address: trimmed, status: "unreachable-or-untrusted" });
            this.#render();
        });
    }
    /**
     * Re-attaches whatever `trustMode` a prior `"verified"` result proved for `address`
     * (Tier-0 trusted-IPv6 e2e plan, Task 2) — a no-op when nothing has proven it yet, which
     * keeps every existing manual-discovery outcome shape unchanged.
     */
    #withKnownTrustMode(address, state) {
        const trustMode = this.#manualTrustModeByAddress.get(address);
        return trustMode === undefined ? state : { ...state, trustMode };
    }
    /**
     * Fail-closed gate for the discovery screen's `set-up` action (§7/§3 of the umbrella
     * spec): advances — and promotes the candidate as the home-node route — only when the
     * live discovery view currently holds a `"verified"` candidate. A stray `set-up` with no
     * verified candidate (shouldn't be reachable from the UI, since the screen only renders
     * the button once a candidate is present, but this is the actual enforcement point) is a
     * silent no-op, never "continue anyway".
     */
    #confirmDiscoveredNode() {
        const verified = [...this.#discoveryRows.values()].find((row) => row.status === "verified");
        if (verified === undefined)
            return;
        if (this.#client !== undefined) {
            void this.#client.command(DISCOVERY_PROMOTE_COMMAND, { address: verified.address }).catch(() => undefined);
        }
        this.#applyCommand({ kind: "advance-discovery" });
    }
    #discoveryState() {
        const manual = this.#manualDiscovery;
        const manualField = manual === undefined ? {} : { manual };
        const rows = [...this.#discoveryRows.values()];
        const verified = rows.find((row) => row.status === "verified" && row.node !== undefined);
        if (verified?.node !== undefined) {
            return {
                candidate: {
                    displayName: verified.node.displayName,
                    maskedId: verified.node.maskedNodeId,
                    statusLabel: "READY TO CLAIM",
                },
                steps: DISCOVERY_STEPS_FOUND,
                ...manualField,
            };
        }
        if (rows.length === 0)
            return { steps: DEFAULT_DISCOVERY_STEPS, ...manualField };
        const anyProbing = rows.some((row) => row.status === "probing");
        const anyReachable = rows.some((row) => row.status === "reachable");
        return {
            steps: [
                { label: "LISTENING ON YOUR NETWORK", meta: "MDNS", status: "done" },
                {
                    label: "QUERYING NEARBY DEVICES",
                    meta: "SUBNET",
                    status: anyProbing ? "active" : "done",
                },
                {
                    label: "CHECKING WHO OWNS IT",
                    status: !anyProbing && anyReachable ? "active" : "pending",
                },
            ],
            ...manualField,
        };
    }
    #render() {
        const root = this.shadowRoot;
        if (root === null)
            return;
        const view = this.#controller.view;
        const chrome = root.querySelector(CHROME_TAG);
        if (chrome)
            chrome.phase = view.phase;
        const outlet = root.querySelector("[data-screen-outlet]");
        if (outlet === null)
            return;
        const tag = SCREEN_TAG_BY_SCREEN[view.screen];
        if (tag === undefined) {
            outlet.replaceChildren();
            return;
        }
        const current = outlet.firstElementChild;
        const screen = current !== null && current.tagName.toLowerCase() === tag
            ? current
            : (() => {
                outlet.replaceChildren();
                const created = document.createElement(tag);
                outlet.append(created);
                return created;
            })();
        this.#applyScreenState(screen, view);
    }
    #applyScreenState(screen, view) {
        switch (view.screen) {
            case "scan":
                screen.state = this.#discoveryState();
                return;
            case "invite":
                screen.state = {};
                return;
            case "identity":
                screen.state = {};
                return;
            case "matchcode":
                screen.state = matchcodeStateFromView(view);
                return;
            case "done":
                screen.state = {};
                return;
            case "handoff":
                return;
        }
    }
}
function manualDiscoveryFromResult(address, result) {
    switch (result.status) {
        case "verified":
            return { address, candidate: result.candidate, status: "verified" };
        case "reached-but-invalid":
            return { address, reason: result.reason, status: "reached-but-invalid" };
        case "unreachable-or-untrusted":
            return { address, status: "unreachable-or-untrusted" };
    }
}
function matchcodeStateFromView(view) {
    return {
        code: (view.matchcodeDigits ?? []).join(""),
        status: view.approvalState === "approved" ? "confirmed" : "wait",
    };
}
export function registerOnboardingElements() {
    define(CHROME_TAG, NodeOnboardingChromeElement);
    define(DISCOVERY_TAG, NodeOnboardingConnectDiscoveryElement);
    define(INVITATION_TAG, NodeOnboardingIdentifyInvitationElement);
    define(IDENTITY_TAG, NodeOnboardingIdentifyIdentityElement);
    define(MATCHCODE_TAG, NodeOnboardingSecureMatchcodeElement);
    define(READY_TAG, NodeOnboardingReadyCompleteElement);
    define(GATE_TAG, NodeOnboardingGateElementImpl);
}
function define(name, constructor) {
    if (!customElements.get(name)) {
        customElements.define(name, constructor);
    }
}
//# sourceMappingURL=onboarding-gate.js.map